import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useClient } from '@/contexts/ClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { Building2, Users, Box, DollarSign, Shield } from 'lucide-react';
import { ClientUsers } from './ClientUsers';
import { ClientConnectors } from './ClientConnectors';
import { ClientBilling } from './ClientBilling';
import { AccessLevelBadge } from '@/components/AccessLevelBadge';
import { RequestAdminDialog } from '@/components/RequestAdminDialog';

type TabId = 'overview' | 'users' | 'connectors' | 'billing';

export function ClientDetail() {
  const { clientId } = useParams<{ clientId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as TabId) || 'overview';
  const { currentClient, setCurrentClient } = useClient();
  const { user } = useAuth();
  const [isRequestDialogOpen, setIsRequestDialogOpen] = useState(false);

  const { data: client, isLoading, error } = useQuery({
    queryKey: ['client-detail', clientId],
    queryFn: () => api.getClientDetail(clientId!),
    enabled: !!clientId,
  });

  // Update context when client loads
  if (client && (!currentClient || currentClient.client_id !== clientId)) {
    setCurrentClient(client);
  }

  const setActiveTab = (tab: TabId) => {
    setSearchParams({ tab });
  };

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="skeleton">
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <div>
            <div className="h-4 w-32 bg-slate-700 rounded animate-pulse mb-2"></div>
            <div className="h-8 w-64 bg-slate-700 rounded animate-pulse mb-2"></div>
            <div className="h-4 w-48 bg-slate-700 rounded animate-pulse"></div>
          </div>
          <div className="h-10 w-40 bg-slate-700 rounded animate-pulse"></div>
        </div>
        {/* Tabs skeleton */}
        <div className="border-b border-slate-700 pb-4 flex gap-8">
          <div className="h-8 w-24 bg-slate-700 rounded animate-pulse"></div>
          <div className="h-8 w-24 bg-slate-700 rounded animate-pulse"></div>
          <div className="h-8 w-24 bg-slate-700 rounded animate-pulse"></div>
        </div>
        {/* Content skeleton */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
          <div className="h-6 w-48 bg-slate-700 rounded animate-pulse mb-4"></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="h-12 bg-slate-700 rounded animate-pulse"></div>
            <div className="h-12 bg-slate-700 rounded animate-pulse"></div>
            <div className="h-12 bg-slate-700 rounded animate-pulse"></div>
            <div className="h-12 bg-slate-700 rounded animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !client) {
    // Parse error type from response
    const errorMessage = (error as Error)?.message || 'Not found';
    const is403 = errorMessage.includes('403') || errorMessage.toLowerCase().includes('forbidden') || errorMessage.toLowerCase().includes('access denied');
    const is404 = errorMessage.includes('404') || errorMessage.toLowerCase().includes('not found');

    return (
      <div className="flex flex-col items-center justify-center h-96 space-y-4">
        <div className="text-6xl">{is403 ? '🔒' : is404 ? '🔍' : '⚠️'}</div>
        <h2 className="text-xl font-semibold text-slate-100">
          {is403 ? 'Access Denied' : is404 ? 'Client Not Found' : 'Error Loading Client'}
        </h2>
        <p className="text-slate-400 text-center max-w-md">
          {is403
            ? "You don't have permission to view this client. Please contact your administrator if you believe this is an error."
            : is404
            ? "The client you're looking for doesn't exist or has been removed."
            : `Error: ${errorMessage}`
          }
        </p>
        <button
          onClick={() => window.location.href = '/'}
          className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  const tabs = [
    { id: 'overview' as TabId, label: 'Overview', icon: Building2, visible: true },
    { id: 'users' as TabId, label: 'Users', icon: Users, visible: client.permissions.can_manage_users },
    { id: 'connectors' as TabId, label: 'Connectors', icon: Box, visible: client.permissions.can_manage_connectors },
    { id: 'billing' as TabId, label: 'Billing', icon: DollarSign, visible: client.permissions.can_manage_billing },
  ];

  const visibleTabs = tabs.filter(tab => tab.visible);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-2">
            <a href="/dashboard" className="hover:text-slate-300">Dashboard</a>
            <span>/</span>
            <span className="text-slate-300">{client.client_name}</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-100">{client.client_name}</h1>
          <p className="mt-1 text-sm text-slate-400">{client.client_email}</p>
        </div>

        {/* Access Level Badge & Request Admin Button */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsRequestDialogOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-red-700 bg-red-900/20 hover:bg-red-900/30 text-red-400 hover:text-red-300 transition-colors"
          >
            <Shield className="h-4 w-4" />
            <span className="text-sm font-medium">Request Admin Access</span>
          </button>
          <AccessLevelBadge clientId={clientId!} />
        </div>
      </div>

      {/* Request Admin Dialog */}
      <RequestAdminDialog
        clientId={clientId!}
        clientName={client.client_name}
        partnerTier={user?.partner_tier || 'free_partner'}
        isOpen={isRequestDialogOpen}
        onClose={() => setIsRequestDialogOpen(false)}
      />

      {/* Tabs */}
      <div className="border-b border-slate-700">
        <div role="tablist" aria-label="Client sections" className="flex space-x-8">
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={isActive}
                aria-controls={`tabpanel-${tab.id}`}
                id={`tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 px-1 py-4 border-b-2 font-medium text-sm transition-colors
                  ${isActive
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-slate-400 hover:text-slate-300 hover:border-slate-600'
                  }
                `}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Client Info Card */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
              <h2 className="text-xl font-semibold text-slate-100 mb-4">Client Information</h2>
              <dl className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-slate-500">Company Name</dt>
                  <dd className="mt-1 text-sm text-slate-300">{client.client_name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-500">Email</dt>
                  <dd className="mt-1 text-sm text-slate-300">{client.client_email}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-500">Status</dt>
                  <dd className="mt-1">
                    {client.is_active ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-500/10 text-slate-400 border border-slate-500/20">
                        Inactive
                      </span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-500">Client Since</dt>
                  <dd className="mt-1 text-sm text-slate-300">
                    {new Date(client.created_at).toLocaleDateString()}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Permissions Card */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
              <h2 className="text-xl font-semibold text-slate-100 mb-4">Your Permissions</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-300">Manage Connectors</span>
                  {client.permissions.can_manage_connectors ? (
                    <span className="text-emerald-400 text-sm font-medium">Granted</span>
                  ) : (
                    <span className="text-slate-500 text-sm">Not granted</span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-300">Manage Users</span>
                  {client.permissions.can_manage_users ? (
                    <span className="text-emerald-400 text-sm font-medium">Granted</span>
                  ) : (
                    <span className="text-slate-500 text-sm">Not granted</span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-300">Manage Billing</span>
                  {client.permissions.can_manage_billing ? (
                    <span className="text-emerald-400 text-sm font-medium">Granted</span>
                  ) : (
                    <span className="text-slate-500 text-sm">Not granted</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && client.permissions.can_manage_users && (
          <ClientUsers clientId={clientId!} />
        )}

        {activeTab === 'connectors' && client.permissions.can_manage_connectors && (
          <ClientConnectors clientId={clientId!} />
        )}

        {activeTab === 'billing' && client.permissions.can_manage_billing && (
          <ClientBilling clientId={clientId!} />
        )}
      </div>
    </div>
  );
}
